import { useState, useEffect, useRef } from 'react';
import useAuth from '../../hooks/useAuth.js';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import paises from '../../paises.json';
import './ScrolledHeader.css';
import logo from '../../assets/jovilogo.png';
import CurrencyDropdown from '../CurrencyDropdown.jsx';
import { FaHeart, FaBalanceScale } from 'react-icons/fa';
import { useFavoritesCount } from '../../hooks/useFavoritesCount';

const ScrolledHeader = () => {
  const { isLoggedIn, user, logout } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownBtnStyle = { width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 15 };

  // Dropdown handlers
  const toggleDropdown = () => setIsDropdownOpen((v) => !v);
  const closeDropdown = () => setIsDropdownOpen(false);
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    }
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const handleDashboard = () => {
    closeDropdown();
    window.location.href = '/dashboard';
  };
  const handleProfile = () => {
    closeDropdown();
    window.location.href = '/dashboard/profile';
  };
  const handleLogout = () => {
    logout();
    closeDropdown();
    window.location.href = '/';
  };

  const { t, i18n } = useTranslation();
  const location = useLocation();
  // Proteção temporária: não quebre se contexto global não existir
  const globalState = useGlobalState();
  const handleOpenLogin = globalState?.handleOpenLogin || (() => {});
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [currentCountry, setCurrentCountry] = useState('br');
  const [searchTerm, setSearchTerm] = useState('');
  const favCount = useFavoritesCount();
  const [cmpCount, setCmpCount] = useState(0);

  // Atualiza contador de comparação
  useEffect(() => {
    const loadCompareCount = () => {
      try {
        const cmps = JSON.parse(localStorage.getItem('compare_items') || '[]');
        setCmpCount(Array.isArray(cmps) ? cmps.length : 0);
      } catch {
        setCmpCount(0);
      }
    };
    loadCompareCount();
    const onStorage = (e) => {
      if (e.key === 'compare_items') loadCompareCount();
    };
    const onFocus = () => loadCompareCount();
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Filtrar países baseado no termo de busca
  const filtrarPaises = () => {
    if (!searchTerm) return paises.continents;

    const termoBusca = searchTerm.toLowerCase();
    const regioesFiltradas = {};

    Object.entries(paises.continents).forEach(([regiao, paisesDaRegiao]) => {
      const paisesFiltrados = paisesDaRegiao.filter(pais => 
        pais.nome.toLowerCase().includes(termoBusca) ||
        pais.idioma.toLowerCase().includes(termoBusca) ||
        pais.moeda.toLowerCase().includes(termoBusca)
      );

      if (paisesFiltrados.length > 0) {
        regioesFiltradas[regiao] = paisesFiltrados;
      }
    });

    return regioesFiltradas;
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsLanguageModalOpen(false);
  };

  const toggleLanguageModal = () => {
    setIsLanguageModalOpen(!isLanguageModalOpen);
  };

  // Lock de scroll no body quando o menu mobile estiver aberto
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = prev || '';
    }
    return () => {
      document.body.style.overflow = prev || '';
    };
  }, [isMenuOpen]);

  // Resolve o código de idioma suportado pelo i18n a partir do país/idioma selecionado
  const resolveLanguage = (pais) => {
    const code = (pais?.code || '').toUpperCase();
    const idioma = (pais?.idioma || '').toLowerCase();
    if (idioma.includes('portugu')) return code === 'BR' ? 'pt-BR' : 'pt-PT';
    if (idioma.includes('english') || idioma.includes('ingl')) return 'en-US';
    if (idioma.includes('espa') || idioma.includes('castell')) return 'es-ES';
    if (idioma.includes('fran') || idioma.includes('français') || idioma.includes('francais')) return 'fr-FR';
    // Fallback por país
    const ptCountries = ['BR','PT','MZ','GW','AO','CV','ST','TL'];
    if (ptCountries.includes(code)) return code === 'BR' ? 'pt-BR' : 'pt-PT';
    const esCountries = ['ES','MX','AR','CL','CO','PE','UY','PY','DO','EC','SV','NI','GT','CR','VE','BO','GQ'];
    if (esCountries.includes(code)) return 'es-ES';
    const frCountries = ['FR','BE','CH','LU','CM','CI','SN','ML','CF','MA','NE','TN'];
    if (frCountries.includes(code)) return 'fr-FR';
    const enCountries = ['US','GB','AU','NZ','CA','IE'];
    if (enCountries.includes(code)) return 'en-US';
    return 'en-US';
  };

  const handleLanguageChange = (pais) => {
    const lang = resolveLanguage(pais);
    i18n.changeLanguage(lang);
    try {
      localStorage.setItem('lang', lang);
      localStorage.setItem('country', (pais.code || '').toLowerCase());
    } catch {
      // ignore
    }
    setCurrentCountry((pais.code || '').toLowerCase());
    setIsLanguageModalOpen(false);
  };

  // Mantém bandeira e idioma sincronizados ao carregar/atualizar
  useEffect(() => {
    try {
      const savedCountry = localStorage.getItem('country');
      const langNow = String(i18n.language || '').trim();
      if (savedCountry) {
        setCurrentCountry(savedCountry);
      } else if (langNow) {
        const map = { 'pt-PT': 'pt', 'pt-BR': 'br', 'en-US': 'us', 'es-ES': 'es', 'fr-FR': 'fr' };
        setCurrentCountry(map[langNow] || 'us');
      }
    } catch {
      // ignore
    }
  }, [i18n.language]);

  // Quando o idioma mudar em qualquer lugar (bootstrap/domínio/header), sincroniza a bandeira
  useEffect(() => {
    const map = { 'pt-PT': 'pt', 'pt-BR': 'br', 'en-US': 'us', 'es-ES': 'es', 'fr-FR': 'fr' };
    const handler = (lng) => {
      try {
        const next = map[String(lng || '').trim()] || 'us';
        setCurrentCountry(next);
      } catch {
      // ignore
    }
    };
    try {
      i18n.on('languageChanged', handler);
    } catch {
      // ignore
    }
    return () => {
      try { i18n.off('languageChanged', handler); } catch {
        // ignore
      }
    };
  }, [i18n]);

  return (
    <>
    <header className={`header scrolled ${isMenuOpen ? 'menu-open' : ''}`}>
      <div className="header-content">
        {/* Dropdown customizado de moedas */}
        <Link to="/" className="logo">
          <img src={logo} alt="JoviPlanet" />
        </Link>

        <nav className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
          <ul>
            <li>
              <Link to="/about" className={location.pathname === '/about' ? 'active' : ''}>
                {t('header.about')}
              </Link>
            </li>
           {/*  <li>
              <Link to="/plans" className={location.pathname === '/plans' ? 'active' : ''}>
                Preços
              </Link>
            </li> */}
            <li>
              <Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''}>
                {t('header.contact')}
              </Link>
            </li>
          </ul>
        </nav>

        <div className={`header-actions ${isMenuOpen ? 'active' : ''}`}>
          {(favCount > 0 || cmpCount > 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginRight: 8 }}>
              {favCount > 0 && (
                <button
                  type="button"
                  onClick={() => navigate('/favorites')}
                  title="Favoritos"
                  style={{ position: 'relative', border: 'none', background: 'transparent', cursor: 'pointer' }}
                >
                  <FaHeart style={{ color: '#BA824F', fontSize: 20 }} />
                  <span style={{ position: 'absolute', top: -6, right: -8, background: '#BA824F', color: '#fff', borderRadius: 10, padding: '0 6px', fontSize: 11, lineHeight: '18px', minWidth: 18, textAlign: 'center' }}>{favCount}</span>
                </button>
              )}
              {cmpCount > 0 && (
                <button
                  type="button"
                  onClick={() => navigate('/compare')}
                  title="Comparar imóveis"
                  style={{ position: 'relative', border: 'none', background: 'transparent', cursor: 'pointer' }}
                >
                  <FaBalanceScale style={{ color: '#1f2937', fontSize: 20 }} />
                  <span style={{ position: 'absolute', top: -6, right: -8, background: '#111827', color: '#fff', borderRadius: 10, padding: '0 6px', fontSize: 11, lineHeight: '18px', minWidth: 18, textAlign: 'center' }}>{cmpCount}</span>
                </button>
              )}
            </div>
          )}
          <button 
            className="flag-button notranslate"
            onClick={toggleLanguageModal}
          >
            <img
              src={`/flags/${currentCountry}.png`}
              alt="País selecionado"
            />
          </button>

          <div className="currency-select notranslate">
            <CurrencyDropdown />
          </div>

          {isLoggedIn ? (
            <div className="user-dropdown-wrapper" ref={dropdownRef} style={{ position: 'relative', display: 'inline-block', marginLeft: 12 }}>
              <button className="user-button" onClick={toggleDropdown} style={{ padding: '10px 16px', borderRadius: 8, background: '#f3f3f3', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>{user?.nome || 'Usuário'}</span>
                <span style={{ fontSize: 12 }}>{isDropdownOpen ? '▲' : '▼'}</span>
              </button>
              {isDropdownOpen && (
                <div className="user-dropdown" style={{ position: 'absolute', right: 0, top: '110%', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', borderRadius: 8, zIndex: 999, minWidth: 180 }}>
                  <button onClick={handleDashboard} style={dropdownBtnStyle}>Dashboard</button>
                  <button onClick={handleProfile} style={dropdownBtnStyle}>{t('common.profile', { defaultValue: 'Perfil' })}</button>
                  <button onClick={handleLogout} style={dropdownBtnStyle}>{t('common.logout', { defaultValue: 'Sair' })}</button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={handleOpenLogin} className="login-button">
              {t('header.login')}
            </button>
          )}

          <Link to="/plans" className="host-button" style={{ marginLeft: 16 }}>
            {t('header.becomeHost')}
          </Link>
        </div>

        <button 
          className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>

      {isLanguageModalOpen && (
        <div className="language-modal-overlay" onClick={toggleLanguageModal}>
          <div className="language-modal" onClick={e => e.stopPropagation()}>
            <div className="language-modal-header">
              <h2>{t('header.select_country')}</h2>
              <button className="close-button" onClick={toggleLanguageModal}>
                <span>×</span>
              </button>
            </div>
            <div className="language-search">
              <input
                type="text"
                placeholder={t('header.search_country')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="language-list">
              {Object.entries(filtrarPaises()).map(([regiao, paises]) => (
                <div key={regiao} className="language-region">
                  <h3>{regiao}</h3>
                  <div className="language-buttons">
                    {paises.map((pais) => (
                      <button
                        key={`${pais.code}-${pais.idioma}`}
                        className={`language-button ${currentCountry === pais.code.toLowerCase() ? 'active' : ''}`}
                        onClick={() => handleLanguageChange(pais)}
                      >
                        <img
                          src={`/flags/${pais.code.toLowerCase()}.png`}
                          alt={pais.nome}
                        />
                        <span>{pais.nome}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScrolledHeader;